import { useRef } from 'react';
import { Navigate } from 'react-router-dom';

import Auth from '@/pages/Auth/Auth';
import { useAuth } from '@/store/auth/auth.store';
import { consumeRedirectAfterAuth } from '@/utils/auth-redirect';

/**
 * The sign-in screen, guarded against viewers who are already signed in: a new tab
 * restores the session from the stored tokens, so landing on /auth there meant being
 * asked to sign in again while signed in. Waits out `isInitializing` — before it
 * settles `isLoggedIn` is false for everyone, and redirecting on that would bounce a
 * genuinely signed-out viewer away from the form they came for.
 *
 * Biometric login keeps the form on purpose: `initialize` leaves that session locked
 * rather than restoring it, so `isLoggedIn` stays false until Face ID / Touch ID.
 *
 * Onboarding is not checked here — Layout sends a not-yet-onboarded session on to
 * /onboarding, so /dashboard is the right first stop either way.
 */
export const AuthRoute = () => {
  const isLoggedIn = useAuth((state) => state.isLoggedIn);
  const isInitializing = useAuth((state) => state.isInitializing);
  // Reading it clears it, so it is read once per mount rather than on every render —
  // StrictMode renders twice, and the second pass would find it already spent.
  const targetRef = useRef<string | null>(null);

  if (isInitializing) return null;

  if (isLoggedIn) {
    targetRef.current ??= consumeRedirectAfterAuth() ?? '/dashboard';
    return <Navigate to={targetRef.current} replace />;
  }

  return <Auth />;
};
